/**
 * UserDetailDialog Component - Dialog showing all courses for a user
 */
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { 
  User,
  Building,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import { colors } from './constants';

const UserDetailDialog = ({ open, onOpenChange, selectedUser }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" style={{ color: colors.blue }} />
            {selectedUser?.name || 'Benutzer'}
          </DialogTitle>
          <DialogDescription>
            {selectedUser?.team && (
              <span className="flex items-center gap-1">
                <Building className="h-3 w-3" />
                {selectedUser.team}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[400px] pr-4">
          {selectedUser?.loading ? (
            <LoadingState />
          ) : selectedUser?.error ? (
            <ErrorState error={selectedUser.error} />
          ) : selectedUser?.courses?.length > 0 ? (
            <CoursesList courses={selectedUser.courses} summary={selectedUser.summary} />
          ) : (
            <EmptyState />
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const LoadingState = () => (
  <div className="flex items-center justify-center py-8">
    <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.blue }} />
    <span className="ml-3" style={{ color: colors.gray }}>Lade Kursdaten...</span>
  </div>
);

const ErrorState = ({ error }) => (
  <div className="text-center py-8">
    <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-orange-500" />
    <p style={{ color: colors.gray }}>{error}</p>
  </div>
);

const EmptyState = () => (
  <p className="text-center py-8" style={{ color: colors.gray }}>
    Keine Kursdaten gefunden
  </p>
);

const CoursesList = ({ courses, summary }) => (
  <div className="space-y-3">
    {/* Summary Header */}
    <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: colors.bg }}>
      <span className="font-medium" style={{ color: colors.dark }}>
        {courses.length} Kurs(e)
      </span>
      <div className="flex items-center gap-4 text-sm">
        {summary?.valid > 0 && (
          <span className="flex items-center gap-1 text-green-600">
            <CheckCircle className="h-4 w-4" />
            {summary.valid} gültig
          </span>
        )}
        {summary?.expired > 0 && (
          <span className="flex items-center gap-1 text-red-600">
            <Clock className="h-4 w-4" />
            {summary.expired} abgelaufen
          </span>
        )}
        {summary?.open > 0 && (
          <span className="flex items-center gap-1 text-orange-600">
            <XCircle className="h-4 w-4" />
            {summary.open} offen
          </span>
        )}
      </div>
    </div>
    
    {/* Course List - sorted by status: expired, open, valid */}
    {[...courses]
      .sort((a, b) => {
        const order = { 'expired': 0, 'open': 1, 'valid': 2 };
        return (order[a.status] ?? 3) - (order[b.status] ?? 3);
      })
      .map((course, idx) => (
        <CourseItem key={idx} course={course} />
      ))}
  </div>
);

const CourseItem = ({ course }) => {
  const statusConfig = {
    valid: { bg: 'bg-green-50', border: 'border-green-200', Icon: CheckCircle, iconColor: 'text-green-600' },
    expired: { bg: 'bg-red-50', border: 'border-red-200', Icon: Clock, iconColor: 'text-red-600' },
    open: { bg: 'bg-orange-50', border: 'border-orange-200', Icon: XCircle, iconColor: 'text-orange-600' }
  };
  
  const config = statusConfig[course.status] || statusConfig.open;
  const { Icon } = config;

  return (
    <div className={`p-3 rounded-lg border ${config.bg} ${config.border}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <p className="font-medium" style={{ color: colors.dark }}>
            {course.course_name || 'Unbekannter Kurs'}
          </p>
          <div className="mt-2 text-xs space-y-1" style={{ color: colors.gray }}>
            {course.completion_date && (
              <p><strong>Abgeschlossen:</strong> {course.completion_date}</p>
            )}
            {course.invited_date && (
              <p><strong>Eingeladen:</strong> {course.invited_date}</p>
            )}
            {course.expiry_date && (
              <p><strong>Ablauf:</strong> {course.expiry_date}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailDialog;
